import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { testId } from '../../app/testId';
import { focusableIn, focusInto, restoreFocus, trapTabKey } from '../a11y/focus';
import { useEscapeLayer } from '../a11y/layers';
import { Button } from '../Button/Button';
import { cx } from '../cx';
import { visuallyHiddenClassName } from '../VisuallyHidden/VisuallyHidden';
import styles from './Modal.module.css';

// Modal — DESIGN_SYSTEM §12 row 30 (`.overlay` > `.modal`): for decisions only (Review & publish, "Remove
// this ad?"). Focus is trapped (Tab cycles), `aria-modal`, Escape and a scrim click cancel — except while
// publishing (`dismissible={false}`), when the close button goes too — the page behind stops scrolling, and
// focus returns to whatever had it when the modal opened (TEST_PLAN §1.6 K6). The header carries a slot for
// the mode chip: every surface that covers the shell repeats it (REQ-006 AC5, `review.mode-chip`); the chip
// itself is the shell's. Enters with the 320 ms fade + rise of §7; base.css zeroes it under reduced motion.

export interface ModalProps {
  open: boolean;
  /** Called on Escape, scrim click and the close button. The caller decides what "cancel" means. */
  onClose: () => void;
  title: string;
  /** One line under the title; also the dialog's description. */
  subtitle?: ReactNode;
  children?: ReactNode;
  /** Right-aligned buttons. */
  footer?: ReactNode;
  /** A note on the footer's left ("You can undo right after publishing"). */
  footerNote?: ReactNode;
  /** 420 · 560 (default) · 720 px wide. */
  size?: 'sm' | 'md' | 'lg';
  /** `alertdialog` for a destructive confirm. */
  role?: 'dialog' | 'alertdialog';
  /** `false` while publishing: nothing closes it until the caller says so. */
  dismissible?: boolean;
  /** The mode chip (REQ-006 AC5), rendered in the header. */
  modeChip?: ReactNode;
  /**
   * The panel draws its own top row and the header band is not rendered — the command palette, whose first
   * row IS the search field (DESIGN_SYSTEM §12 row 31). `title` still names the dialog; it is only hidden
   * from sight, never from the accessibility tree, and the caller owes its own way to close with a pointer.
   */
  hideHeader?: boolean;
  /** For a panel that is not a centred 560 px card: the palette pins itself high and goes full-screen at 360. */
  overlayClassName?: string;
  /**
   * For a body that is not a padded, scrolling column of prose — the palette's body is a flush flex column
   * whose middle section scrolls and whose foot is pinned (DESIGN_SYSTEM §12 row 31).
   */
  bodyClassName?: string;
  /** Focus lands here on open; default: the first tabbable element that is not the close button. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Focus returns here on close; default: whatever had focus when the modal opened (the trigger). */
  returnFocusRef?: RefObject<HTMLElement | null>;
  closeLabel?: string;
  /** Test id of the dialog; the close button is `<testID>.close`. */
  testID?: string;
  /**
   * An id on the TITLE itself, for a dialog whose heading is data QA reads — the creatives player's
   * `creatives.player.name` is the ad's own name (T-304, TEST_PLAN §1.4). Most dialogs are titled with a
   * constant and need none.
   */
  titleTestID?: string;
  id?: string;
  className?: string;
}

let scrollLocks = 0;
let savedOverflow = '';
let savedPaddingRight = '';

function lockScroll() {
  if (scrollLocks === 0) {
    const body = document.body;
    savedOverflow = body.style.overflow;
    savedPaddingRight = body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
  }
  scrollLocks += 1;
}
function unlockScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) {
    document.body.style.overflow = savedOverflow;
    document.body.style.paddingRight = savedPaddingRight;
  }
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  footerNote,
  size = 'md',
  role = 'dialog',
  dismissible = true,
  modeChip,
  hideHeader = false,
  overlayClassName,
  bodyClassName,
  initialFocusRef,
  returnFocusRef,
  closeLabel = 'Close',
  testID,
  titleTestID,
  id,
  className,
}: ModalProps) {
  const generated = useId();
  const base = id ?? `modal-${generated}`;
  const titleId = `${base}-title`;
  const subtitleId = `${base}-subtitle`;
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const downOnScrim = useRef(false);

  useEscapeLayer(open && dismissible, onClose);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    lockScroll();
    const panel = panelRef.current;
    if (panel) {
      const preferred =
        initialFocusRef?.current ??
        focusableIn(panel).find((el) => el !== closeRef.current) ??
        null;
      focusInto(panel, preferred);
    }
    return () => {
      unlockScroll();
      restoreFocus(returnFocusRef?.current ?? previous);
    };
  }, [open, initialFocusRef, returnFocusRef]);

  if (!open) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (panelRef.current) trapTabKey(event, panelRef.current);
  };
  const onScrimPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    downOnScrim.current = event.target === event.currentTarget;
  };
  const onScrimClick = (event: MouseEvent<HTMLDivElement>) => {
    if (dismissible && downOnScrim.current && event.target === event.currentTarget) onClose();
    downOnScrim.current = false;
  };

  return createPortal(
    <div
      className={cx(styles.overlay, overlayClassName)}
      onPointerDown={onScrimPointerDown}
      onClick={onScrimClick}
      data-testid={testID ? testId(testID, 'overlay') : undefined}
    >
      <div
        ref={panelRef}
        id={base}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        tabIndex={-1}
        className={cx(
          styles.modal,
          size === 'sm' && styles.sm,
          size === 'lg' && styles.lg,
          className,
        )}
        data-testid={testID}
        onKeyDown={onKeyDown}
      >
        {hideHeader ? (
          <div className={visuallyHiddenClassName}>
            <h2 id={titleId} data-testid={titleTestID}>
              {title}
            </h2>
            {subtitle ? <p id={subtitleId}>{subtitle}</p> : null}
          </div>
        ) : (
          <div className={styles.header}>
            <div className={styles.heading}>
              <h2 id={titleId} className={styles.title} data-testid={titleTestID}>
                {title}
              </h2>
            </div>
            {modeChip || dismissible ? (
              <div className={styles.tools}>
                {modeChip}
                {dismissible ? (
                  <Button
                    ref={closeRef}
                    variant="ghost"
                    size="sm"
                    iconOnly
                    icon="x"
                    aria-label={closeLabel}
                    testID={testID ? testId(testID, 'close') : undefined}
                    onClick={onClose}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        )}
        {/* BUG-102 (§12 no. 30, the web twin of T-105's `ui/Sheet` fix): the head is `title + chip + ×` and the
            subtitle is the head's SIBLING at the surface's full measure — inside the title column it kept ~60 % of
            the width at 360 and set in five lines against an empty gutter. */}
        {!hideHeader && subtitle ? (
          <p id={subtitleId} className={styles.subtitle}>
            {subtitle}
          </p>
        ) : null}
        {children ? <div className={cx(styles.body, bodyClassName)}>{children}</div> : null}
        {footer || footerNote ? (
          <div className={styles.footer}>
            {footerNote ? <span className={styles.footerNote}>{footerNote}</span> : null}
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
